/**
 * usePeerStats — Live WebRTC stats and media health of one peer.
 *
 * Enables stats collection on the PeerConnection while mounted (reference
 * counted, so several components can watch the same peer) and mirrors the
 * STATS_UPDATED / MEDIA_STATE_CHANGED events into React state:
 *
 *   {
 *     sample,   // latest normalized sample (peerStatsParser) or null
 *     history,  // last `historySize` samples of the CURRENT connection, oldest first
 *     health,   // 'ok' | 'stalled' | 'no-track' | 'unknown'
 *     grade,    // 'good' | 'warning' | 'serious' | 'critical' | null
 *     connectionState, iceState, // raw RTCPeerConnection states at the last sample
 *   }
 *
 * Only the component using this hook re-renders on a stats tick.
 */

import { useState, useEffect } from "react";
import { useEventBus } from "shared/services/eventBus";
import { ROBOT_EVENTS, getRobotEvent } from "shared/services/eventBus/eventTypes";
import { useConnectionManager } from "features/robot-teleop/contexts/RobotConnectionManagerContext";
import { PEER_INTERNAL_EVENTS, PEER_STATES } from "features/robot-teleop/webrtc/peerStates";
import { gradeSample } from "features/robot-teleop/webrtc/peerStatsParser";

const EMPTY = Object.freeze({
  sample: null,
  history: [],
  health: "unknown",
  grade: null,
  healthSince: null, // Date.now() of the last health change (for "no frames for N s")
  connectionState: null,
  iceState: null,
});

const seedFromPeer = (peer, historySize) => {
  if (!peer) return EMPTY;
  const history = peer.statsHistory.slice(-historySize);
  const sample = history.length ? history[history.length - 1] : null;
  return {
    sample,
    history,
    health: peer.videoHealth,
    grade: gradeSample(sample),
    healthSince: peer.videoHealth === "unknown" ? null : Date.now(),
    connectionState: peer.connectionState,
    iceState: peer.iceState,
  };
};

const withHealth = (prev, health) => (
  prev.health === health ? prev : { ...prev, health, healthSince: Date.now() }
);

export const usePeerStats = (peerId, { intervalMs = 1000, historySize = 60 } = {}) => {
  const { manager, peerIds } = useConnectionManager();
  const eventBus = useEventBus();
  const [stats, setStats] = useState(EMPTY);

  // peerIds changes when the manager creates the peers (initialize()), which
  // may happen after this component mounted — re-run then so enableStats()
  // reaches the real PeerConnection.
  const peerKnown = !!peerId && peerIds.includes(peerId);

  useEffect(() => {
    const peer = peerKnown ? manager?.getPeer(peerId) : null;
    setStats(seedFromPeer(peer, historySize));
    if (!peer) return undefined;

    peer.enableStats(intervalMs);

    const unsubStats = eventBus.subscribe(
      getRobotEvent(peerId, PEER_INTERNAL_EVENTS.STATS_UPDATED),
      (payload) => {
        if (!payload || payload.peerId !== peerId) return;
        setStats((prev) => ({
          ...withHealth(prev, payload.health),
          sample: payload.sample,
          history: [...prev.history.slice(-(historySize - 1)), payload.sample],
          grade: payload.grade,
          connectionState: payload.connectionState,
          iceState: payload.iceState,
        }));
      }
    );

    const unsubMedia = eventBus.subscribe(
      getRobotEvent(peerId, ROBOT_EVENTS.MEDIA_STATE_CHANGED),
      (payload) => {
        if (!payload || payload.peerId !== peerId) return;
        setStats((prev) => withHealth(prev, payload.health));
      }
    );

    // A new attempt or a closed/failed peer invalidates the samples of the old connection.
    const unsubState = eventBus.subscribe(
      getRobotEvent(peerId, ROBOT_EVENTS.CONNECTION_STATE),
      ({ state }) => {
        if (state !== PEER_STATES.CONNECTED) {
          setStats((prev) => ({ ...EMPTY, connectionState: prev.connectionState, iceState: prev.iceState }));
        }
      }
    );

    return () => {
      [unsubStats, unsubMedia, unsubState].forEach((unsub) => { if (typeof unsub === "function") unsub(); });
      peer.disableStats();
    };
  }, [peerId, peerKnown, manager, eventBus, intervalMs, historySize]);

  return stats;
};

export default usePeerStats;
